import { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import type { LatLngExpression, LatLngBoundsLiteral } from 'leaflet';

export interface RealMapPin {
  id: string;
  lat: number;
  lng: number;
  iconHtml: string;
  iconSize?: [number, number];
  iconAnchor?: [number, number];
  zIndexOffset?: number;
  title?: string;
}

export interface RealMapLine {
  id: string;
  from: { lat: number; lng: number };
  to: { lat: number; lng: number };
  color?: string;
  weight?: number;
  opacity?: number;
  dashArray?: string;
}

export interface RealMapProps {
  pins: RealMapPin[];
  lines?: RealMapLine[];
  height?: number | string;
  tileTheme?: 'dark' | 'light';
  fallbackCenter?: [number, number];
  fallbackZoom?: number;
  onPinEnter?: (id: string, containerPoint: { x: number; y: number }) => void;
  onPinLeave?: (id: string) => void;
  onPinClick?: (id: string) => void;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

// Esri's keyless raster services, not tile.openstreetmap.org — OSM's donated
// tile server rate-limits production traffic per IP, which shows up as maps
// loading slowly for the client while looking fine in one-off tests. CARTO
// basemaps started watermarking every tile without an API key in 2026.
const TILE_URLS = {
  dark: 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}',
  light: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
};

const TILE_ATTR = {
  dark: 'Tiles &copy; <a href="https://www.esri.com/">Esri</a> &mdash; Esri, HERE, Garmin, OpenStreetMap contributors',
  light: 'Tiles &copy; <a href="https://www.esri.com/">Esri</a> &mdash; Esri, HERE, Garmin, OpenStreetMap contributors',
};

// Icon identity must be stable across parent re-renders: a fresh L.divIcon per
// render makes react-leaflet call setIcon and replace every pin's DOM node,
// which is what made the Dashboard/Projects maps stutter while polling widgets
// re-rendered the page around them.
function PinMarker({
  pin,
  onPinEnter,
  onPinLeave,
  onPinClick,
}: {
  pin: RealMapPin;
  onPinEnter?: RealMapProps['onPinEnter'];
  onPinLeave?: RealMapProps['onPinLeave'];
  onPinClick?: RealMapProps['onPinClick'];
}) {
  const map = useMap();
  const icon = useMemo(
    () => L.divIcon({
      html: pin.iconHtml,
      className: 'cls-divicon',
      iconSize: pin.iconSize ?? [22, 28],
      iconAnchor: pin.iconAnchor ?? [11, 28],
    }),
    [pin.iconHtml, pin.iconSize?.[0], pin.iconSize?.[1], pin.iconAnchor?.[0], pin.iconAnchor?.[1]],
  );
  const eventHandlers = useMemo(() => ({
    mouseover: () => {
      if (!onPinEnter) return;
      const cp = map.latLngToContainerPoint([pin.lat, pin.lng]);
      onPinEnter(pin.id, { x: cp.x, y: cp.y });
    },
    mouseout: () => onPinLeave?.(pin.id),
    click: () => onPinClick?.(pin.id),
  }), [map, pin.id, pin.lat, pin.lng, onPinEnter, onPinLeave, onPinClick]);

  return (
    <Marker
      position={[pin.lat, pin.lng]}
      title={pin.title}
      icon={icon}
      zIndexOffset={pin.zIndexOffset ?? 0}
      eventHandlers={eventHandlers}
    />
  );
}

function MapBody({
  pins,
  lines,
  onPinEnter,
  onPinLeave,
  onPinClick,
  fallbackCenter,
  fallbackZoom,
}: {
  pins: RealMapPin[];
  lines: RealMapLine[];
  onPinEnter?: RealMapProps['onPinEnter'];
  onPinLeave?: RealMapProps['onPinLeave'];
  onPinClick?: RealMapProps['onPinClick'];
  fallbackCenter: [number, number];
  fallbackZoom: number;
}) {
  const map = useMap();

  const boundsKey = useMemo(
    () => pins.map((p) => `${p.id}:${p.lat.toFixed(5)},${p.lng.toFixed(5)}`).sort().join('|'),
    [pins],
  );

  useEffect(() => {
    if (pins.length === 0) {
      map.setView(fallbackCenter, fallbackZoom, { animate: false });
      return;
    }
    if (pins.length === 1) {
      map.setView([pins[0]!.lat, pins[0]!.lng], 12, { animate: false });
      return;
    }
    const bounds: LatLngBoundsLiteral = pins.map((p) => [p.lat, p.lng]);
    map.fitBounds(bounds, { padding: [30, 30], maxZoom: 13, animate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundsKey]);

  useEffect(() => {
    const t = window.setTimeout(() => map.invalidateSize(), 0);
    return () => window.clearTimeout(t);
  }, [map]);

  return (
    <>
      {lines.map((ln) => (
        <Polyline
          key={ln.id}
          positions={[[ln.from.lat, ln.from.lng], [ln.to.lat, ln.to.lng]]}
          pathOptions={{
            color: ln.color ?? 'rgba(232,164,39,.18)',
            weight: ln.weight ?? 1,
            opacity: ln.opacity ?? 1,
            dashArray: ln.dashArray ?? '4,4',
            interactive: false,
          }}
        />
      ))}
      {pins.map((p) => (
        <PinMarker key={p.id} pin={p} onPinEnter={onPinEnter} onPinLeave={onPinLeave} onPinClick={onPinClick} />
      ))}
    </>
  );
}

export function RealMap({
  pins,
  lines = [],
  height = 200,
  tileTheme = 'light',
  fallbackCenter = [51.5, -0.12],
  fallbackZoom = 10,
  onPinEnter,
  onPinLeave,
  onPinClick,
  children,
  className,
  style,
}: RealMapProps) {
  const initialCenter: LatLngExpression = pins[0]
    ? [pins[0].lat, pins[0].lng]
    : fallbackCenter;
  const initialZoom = pins.length > 1 ? 11 : fallbackZoom;

  return (
    <div
      className={`cls-realmap${className ? ` ${className}` : ''}`}
      style={{ position: 'relative', width: '100%', height, ...style }}
    >
      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        zoomControl={false}
        scrollWheelZoom
        doubleClickZoom
        dragging
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer url={TILE_URLS[tileTheme]} attribution={TILE_ATTR[tileTheme]} />
        <MapBody
          pins={pins}
          lines={lines}
          onPinEnter={onPinEnter}
          onPinLeave={onPinLeave}
          onPinClick={onPinClick}
          fallbackCenter={fallbackCenter}
          fallbackZoom={fallbackZoom}
        />
      </MapContainer>
      <div
        className="cls-realmap-overlay"
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 1000 }}
      >
        {children}
      </div>
    </div>
  );
}

export function positionTooltip(
  containerPoint: { x: number; y: number },
  containerEl: HTMLElement | null,
  tooltipWidth = 190,
  tooltipHeight = 85,
): { leftPx: number; topPx: number } {
  const w = containerEl?.clientWidth ?? 800;
  let l = containerPoint.x + 10;
  let t = containerPoint.y - tooltipHeight;
  if (l + tooltipWidth > w - 10) l = containerPoint.x - tooltipWidth - 10;
  if (t < 4) t = 4;
  return { leftPx: l, topPx: t };
}
